import { useState } from 'react';
import MD5 from 'crypto-js/md5';

import request, { setToken } from '@/request';

export default function SignIn({ onSuccess, onForgotPassword }) {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const isFormValid = phone && password;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isFormValid && !isLoading) {
        handleSubmit(e);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsLoading(true);

    try {
      const loginInfo = await request.post('/auth/login', {
        phone: String(phone).trim(),
        password: MD5(String(password).trim()).toString(),
      });

      setToken(loginInfo.accessToken);
      const userInfo = await request.get('/system/user');
      onSuccess?.(userInfo);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
      className="space-y-5"
    >
      {/* Phone Input */}
      <div>
        <label
          htmlFor="phone"
          className="block text-sm font-semibold text-slate-900 mb-3 text-left"
        >
          手机号
        </label>
        <div className="relative">
          <input
            id="phone"
            type="tel"
            placeholder="输入您的手机号"
            value={phone}
            maxLength={11}
            onChange={(e) => setPhone(e.target.value)}
            onFocus={() => setFocusedField('phone')}
            onBlur={() => setFocusedField(null)}
            className={`w-full px-4 py-3 text-base text-slate-900 bg-slate-50 border-2 rounded-lg transition-all duration-200 placeholder:text-slate-400 ${
              focusedField === 'phone'
                ? 'border-blue-500 bg-white shadow-lg shadow-blue-500/10'
                : 'border-slate-200 hover:border-slate-300'
            } focus:outline-none`}
            required
          />
          {focusedField === 'phone' && phone && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <svg
                className="w-5 h-5 text-blue-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Password Input */}
      <div>
        <label
          htmlFor="password"
          className="block text-sm font-semibold text-slate-900 mb-3 text-left"
        >
          密码
        </label>
        <div className="relative">
          <input
            id="password"
            type="password"
            placeholder="输入您的密码"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() => setFocusedField('password')}
            onBlur={() => setFocusedField(null)}
            className={`w-full px-4 py-3 text-base text-slate-900 bg-slate-50 border-2 rounded-lg transition-all duration-200 placeholder:text-slate-400 ${
              focusedField === 'password'
                ? 'border-blue-500 bg-white shadow-lg shadow-blue-500/10'
                : 'border-slate-200 hover:border-slate-300'
            } focus:outline-none`}
            required
          />
          {focusedField === 'password' && password && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <svg
                className="w-5 h-5 text-blue-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Login Button */}
      <button
        type="submit"
        disabled={isLoading || !isFormValid}
        className={`w-full py-3 px-4 font-semibold rounded-lg transition-all duration-200 cursor-pointer flex items-center justify-center gap-2 text-base mt-8 ${
          isFormValid && !isLoading
            ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 hover:shadow-md hover:shadow-blue-500/20 active:from-blue-700 active:to-blue-800'
            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
        }`}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>登录中...</span>
          </>
        ) : (
          <span>登录</span>
        )}
      </button>

      {/* Forgot Password Link */}
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={() => onForgotPassword?.(phone)}
          className="text-sm text-blue-600 hover:underline transition-colors"
        >
          忘记密码
        </button>
      </div>
    </form>
  );
}
